import type { AdminAccount, Api } from "../api/client.ts";
import { ApiError } from "../api/client.ts";
import { planLabel } from "../plan.ts";

/** Duration presets offered when generating a code, in days. */
const DURATIONS: ReadonlyArray<{ readonly label: string; readonly days: number }> = [
  { label: "1 day", days: 1 },
  { label: "1 month", days: 30 },
  { label: "3 months", days: 90 },
  { label: "6 months", days: 180 },
  { label: "12 months (annual)", days: 365 },
];

type Tab = "generate" | "accounts";

/**
 * The admin panel: generate unlock codes, and see/manage accounts.
 *
 * Same hand-built-overlay approach as AuthDialog and PaywallDialog, for the
 * same reason — see authDialog.ts's comment on why a native `<dialog>` is
 * avoided here.
 */
export class AdminDialog {
  private readonly overlay: HTMLElement;
  private readonly card: HTMLElement;
  private readonly tabButtons: HTMLElement;
  private readonly generatePanel: HTMLElement;
  private readonly accountsPanel: HTMLElement;

  private readonly emailInput: HTMLInputElement;
  private readonly planSelect: HTMLSelectElement;
  private readonly daysSelect: HTMLSelectElement;
  private readonly amountInput: HTMLInputElement;
  private readonly generateButton: HTMLButtonElement;
  private readonly generateAndEmailButton: HTMLButtonElement;
  private readonly generateNotice: HTMLElement;
  private readonly resultBox: HTMLElement;
  private readonly resultCode: HTMLInputElement;

  private readonly accountsBody: HTMLElement;
  private readonly accountsNotice: HTMLElement;
  /** Emails whose detail row is expanded, kept across reloads so a save doesn't collapse it. */
  private expandedAccounts = new Set<string>();

  private resolve: (() => void) | null = null;
  private previouslyFocused: HTMLElement | null = null;

  constructor(
    private readonly api: Api,
    private readonly root: Document = document,
  ) {
    this.overlay = root.createElement("div");
    this.overlay.className = "admin-overlay";
    this.overlay.hidden = true;
    this.overlay.innerHTML = `
      <div class="admin" role="dialog" aria-modal="true" aria-labelledby="admin-title">
        <button type="button" class="admin__close" aria-label="Close">&times;</button>
        <h2 class="admin__title" id="admin-title">Admin</h2>

        <div class="admin-tabs" role="tablist">
          <button type="button" class="admin-tabs__btn" data-tab="generate" aria-pressed="true">Generate code</button>
          <button type="button" class="admin-tabs__btn" data-tab="accounts" aria-pressed="false">Accounts</button>
        </div>

        <div class="admin-panel admin-panel--generate">
          <label class="admin__field">
            <span>Email</span>
            <input type="email" autocomplete="off" placeholder="customer@example.com" />
          </label>
          <label class="admin__field">
            <span>Plan</span>
            <select class="admin__plan">
              <option value="day">Day pass</option>
              <option value="pro" selected>Pro</option>
            </select>
          </label>
          <label class="admin__field">
            <span>Length</span>
            <select class="admin__days">
              ${DURATIONS.map((d) => `<option value="${d.days}">${d.label}</option>`).join("")}
            </select>
          </label>
          <label class="admin__field">
            <span>Amount charged (optional)</span>
            <input type="number" class="admin__amount" min="0" step="0.01" placeholder="e.g. 39.00" />
          </label>
          <button type="button" class="btn btn--primary admin__generate">Generate code</button>
          <button type="button" class="btn admin__generate-email">Generate and email code</button>
          <p class="admin__notice admin__notice--generate" hidden></p>
          <div class="admin-result" hidden>
            <p class="admin-result__hint">This code is shown once — copy it now.</p>
            <input type="text" class="admin-result__code" readonly />
          </div>
        </div>

        <div class="admin-panel admin-panel--accounts" hidden>
          <p class="admin__notice admin__notice--accounts" hidden></p>
          <div class="admin-table-wrap">
            <table class="admin-table">
              <thead>
                <tr><th>Email</th><th>Plan</th><th>Expires</th><th>Nests</th><th>Parts</th><th>Last used</th><th></th></tr>
              </thead>
              <tbody></tbody>
            </table>
          </div>
        </div>
      </div>
    `;
    root.body.append(this.overlay);

    this.card = this.overlay.querySelector(".admin")!;
    this.tabButtons = this.overlay.querySelector(".admin-tabs")!;
    this.generatePanel = this.overlay.querySelector(".admin-panel--generate")!;
    this.accountsPanel = this.overlay.querySelector(".admin-panel--accounts")!;

    this.emailInput = this.overlay.querySelector(".admin__field input")!;
    this.planSelect = this.overlay.querySelector(".admin__plan")!;
    this.daysSelect = this.overlay.querySelector(".admin__days")!;
    this.amountInput = this.overlay.querySelector(".admin__amount")!;
    this.generateButton = this.overlay.querySelector(".admin__generate")!;
    this.generateAndEmailButton = this.overlay.querySelector(".admin__generate-email")!;
    this.generateNotice = this.overlay.querySelector(".admin__notice--generate")!;
    this.resultBox = this.overlay.querySelector(".admin-result")!;
    this.resultCode = this.overlay.querySelector(".admin-result__code")!;

    this.accountsBody = this.overlay.querySelector(".admin-table tbody")!;
    this.accountsNotice = this.overlay.querySelector(".admin__notice--accounts")!;

    this.overlay.querySelector(".admin__close")!.addEventListener("click", () => this.close());
    this.tabButtons.addEventListener("click", (event) => {
      const button = (event.target as HTMLElement).closest<HTMLButtonElement>("[data-tab]");
      if (button) void this.setTab(button.dataset["tab"] as Tab);
    });
    this.generateButton.addEventListener("click", () => void this.generate(false));
    this.generateAndEmailButton.addEventListener("click", () => void this.generate(true));
    this.resultCode.addEventListener("focus", () => this.resultCode.select());

    this.overlay.addEventListener("mousedown", (event) => {
      if (event.target === this.overlay) this.close();
    });
  }

  /** Open the panel. Resolves once it's dismissed. */
  open(): Promise<void> {
    void this.setTab("generate");

    this.previouslyFocused = this.root.activeElement as HTMLElement | null;
    this.setBackgroundInert(true);
    this.overlay.hidden = false;
    this.root.addEventListener("keydown", this.handleKeydown, true);
    (this.overlay.querySelector(".admin__close") as HTMLElement).focus();

    return new Promise((resolve) => {
      this.resolve = resolve;
    });
  }

  private async setTab(tab: Tab): Promise<void> {
    for (const button of this.tabButtons.querySelectorAll<HTMLButtonElement>("[data-tab]")) {
      button.setAttribute("aria-pressed", String(button.dataset["tab"] === tab));
    }
    this.generatePanel.hidden = tab !== "generate";
    this.accountsPanel.hidden = tab !== "accounts";

    if (tab === "accounts") await this.loadAccounts();
  }

  private async generate(sendEmail: boolean): Promise<void> {
    const email = this.emailInput.value.trim();
    const plan = this.planSelect.value as "day" | "pro";
    const days = Number(this.daysSelect.value);
    const amount = this.amountInput.value.trim();
    const amountCents = amount === "" ? undefined : Math.round(Number(amount) * 100);
    const durationLabel = this.daysSelect.selectedOptions[0]?.textContent ?? "";
    const planName = `${planLabel(plan)} (${durationLabel})`;

    this.generateButton.disabled = true;
    this.generateAndEmailButton.disabled = true;
    this.showGenerateNotice(null);
    this.resultBox.hidden = true;

    try {
      const { code } = await this.api.admin.generateCode(email, plan, days, amountCents, sendEmail, planName);
      this.resultCode.value = code;
      this.resultBox.hidden = false;
      this.emailInput.value = "";
      this.amountInput.value = "";
    } catch (error) {
      this.showGenerateNotice(
        error instanceof ApiError ? error.message : "Could not reach the server. Try again.",
      );
    } finally {
      this.generateButton.disabled = false;
      this.generateAndEmailButton.disabled = false;
    }
  }

  private async loadAccounts(): Promise<void> {
    this.showAccountsNotice(null);
    this.accountsBody.innerHTML = "";

    let accounts: AdminAccount[];
    try {
      ({ accounts } = await this.api.admin.listAccounts());
    } catch (error) {
      this.showAccountsNotice(
        error instanceof ApiError ? error.message : "Could not reach the server. Try again.",
      );
      return;
    }

    for (const account of accounts) {
      const row = this.root.createElement("tr");
      const expiry = account.planExpiresAt
        ? new Date(account.planExpiresAt).toLocaleDateString()
        : "—";
      const lastUsed = account.lastSplitAt
        ? new Date(account.lastSplitAt).toLocaleString()
        : "—";
      row.innerHTML = `
        <td></td>
        <td>${escapeHtml(planLabel(account.plan))}</td>
        <td>${expiry}</td>
        <td>${account.nestsSplit}</td>
        <td>${account.partsSplit}</td>
        <td>${lastUsed}</td>
        <td></td>
      `;
      const emailCell = row.querySelector("td:first-child")!;
      const emailButton = this.root.createElement("button");
      emailButton.type = "button";
      emailButton.className = "linkbtn admin-table__email";
      emailButton.textContent = account.email;
      emailButton.setAttribute("aria-expanded", "false");
      emailCell.append(emailButton);

      const actionCell = row.querySelector("td:last-child")!;
      actionCell.className = "admin-table__actions";
      const generateCodeButton = this.root.createElement("button");
      generateCodeButton.type = "button";
      generateCodeButton.className = "linkbtn";
      generateCodeButton.textContent = "Generate code";
      generateCodeButton.addEventListener("click", () => void this.startCodeForAccount(account));
      actionCell.append(generateCodeButton);

      const detailRow = this.root.createElement("tr");
      detailRow.className = "admin-record-row";
      detailRow.hidden = !this.expandedAccounts.has(account.email);
      emailButton.setAttribute("aria-expanded", String(!detailRow.hidden));
      const detailCell = this.root.createElement("td");
      detailCell.colSpan = 7;
      detailCell.innerHTML = `
        <dl class="admin-record">
          <div><dt>First name</dt><dd><input type="text" class="admin-record__first" autocomplete="given-name" /></dd></div>
          <div><dt>Last name</dt><dd><input type="text" class="admin-record__last" autocomplete="family-name" /></dd></div>
          <div><dt>Company</dt><dd><input type="text" class="admin-record__company" autocomplete="organization" /></dd></div>
          <div><dt>Account ID</dt><dd>${account.id}</dd></div>
          <div><dt>Admin</dt><dd>${account.isAdmin ? "Yes" : "No"}</dd></div>
        </dl>
        <button type="button" class="btn btn--primary admin-record__save">Save</button>
        <p class="admin__notice admin-record__notice" hidden></p>
      `;
      detailRow.append(detailCell);

      const firstInput = detailCell.querySelector<HTMLInputElement>(".admin-record__first")!;
      const lastInput = detailCell.querySelector<HTMLInputElement>(".admin-record__last")!;
      const companyInput = detailCell.querySelector<HTMLInputElement>(".admin-record__company")!;
      const saveButton = detailCell.querySelector<HTMLButtonElement>(".admin-record__save")!;
      const recordNotice = detailCell.querySelector<HTMLElement>(".admin-record__notice")!;
      firstInput.value = account.firstName;
      lastInput.value = account.lastName;
      companyInput.value = account.company ?? "";

      saveButton.addEventListener("click", () =>
        void this.saveAccountProfile(account, firstInput, lastInput, companyInput, saveButton, recordNotice),
      );

      emailButton.addEventListener("click", () => {
        detailRow.hidden = !detailRow.hidden;
        emailButton.setAttribute("aria-expanded", String(!detailRow.hidden));
        if (detailRow.hidden) this.expandedAccounts.delete(account.email);
        else this.expandedAccounts.add(account.email);
      });

      this.accountsBody.append(row, detailRow);
    }
  }

  /** Jump to the Generate code tab with `account`'s email already filled in, ready to pick a plan and length. */
  private async startCodeForAccount(account: AdminAccount): Promise<void> {
    this.emailInput.value = account.email;
    this.amountInput.value = "";
    this.resultBox.hidden = true;
    this.showGenerateNotice(null);
    await this.setTab("generate");
    this.planSelect.focus();
  }

  private async saveAccountProfile(
    account: AdminAccount,
    firstInput: HTMLInputElement,
    lastInput: HTMLInputElement,
    companyInput: HTMLInputElement,
    saveButton: HTMLButtonElement,
    notice: HTMLElement,
  ): Promise<void> {
    saveButton.disabled = true;
    notice.hidden = true;
    try {
      await this.api.admin.updateAccountProfile(
        account.id,
        firstInput.value.trim(),
        lastInput.value.trim(),
        companyInput.value.trim(),
      );
      this.expandedAccounts.add(account.email);
      await this.loadAccounts();
    } catch (error) {
      notice.textContent =
        error instanceof ApiError ? error.message : "Could not reach the server. Try again.";
      notice.hidden = false;
    } finally {
      saveButton.disabled = false;
    }
  }

  private showGenerateNotice(message: string | null): void {
    this.generateNotice.textContent = message ?? "";
    this.generateNotice.hidden = message === null;
  }

  private showAccountsNotice(message: string | null): void {
    this.accountsNotice.textContent = message ?? "";
    this.accountsNotice.hidden = message === null;
  }

  /** Escape and Tab, the two things a native `<dialog>` handles for free. */
  private readonly handleKeydown = (event: KeyboardEvent): void => {
    if (event.key === "Escape") {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key !== "Tab") return;

    const focusable = Array.from(
      this.card.querySelectorAll<HTMLElement>('button, input, select, a[href], [tabindex]:not([tabindex="-1"])'),
    ).filter((el) => !el.hasAttribute("disabled") && el.offsetParent !== null);
    if (focusable.length === 0) return;

    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;

    if (event.shiftKey && this.root.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && this.root.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  private setBackgroundInert(inert: boolean): void {
    for (const child of Array.from(this.root.body.children)) {
      if (child !== this.overlay) (child as HTMLElement).inert = inert;
    }
  }

  private close(): void {
    this.overlay.hidden = true;
    this.setBackgroundInert(false);
    this.root.removeEventListener("keydown", this.handleKeydown, true);
    this.previouslyFocused?.focus();
    const resolve = this.resolve;
    this.resolve = null;
    resolve?.();
  }
}

function escapeHtml(value: string): string {
  const div = document.createElement("div");
  div.textContent = value;
  return div.innerHTML;
}
